import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../auth/AuthContext';
import { PATIENTS_PATH } from '../constants/routes';
import { formatHostedUiCallbackQueryError } from '../utils/authErrors';

/**
 * Cognito Hosted UI からのリダイレクト先。
 * index で読み込んだ enable-oauth-listener が URL の code を処理したうえでセッションを取得する。
 */
const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthErr = params.get('error');
    if (oauthErr) {
      const rawDesc = params.get('error_description') || '';
      const desc = decodeURIComponent(rawDesc.replace(/\+/g, ' '));
      setError(formatHostedUiCallbackQueryError(oauthErr, desc));
      return undefined;
    }

    let cancelled = false;

    const finish = async () => {
      try {
        const session = await fetchAuthSession();
        if (!session.tokens?.idToken) {
          throw new Error('no id token');
        }
        await refreshUser();
        if (!cancelled) {
          navigate(PATIENTS_PATH, { replace: true });
        }
      } catch {
        if (!cancelled) {
          setError('ログイン処理に失敗しました。ログイン画面からやり直してください。');
        }
      }
    };

    void finish();
    return () => {
      cancelled = true;
    };
  }, [navigate, refreshUser]);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          px: 3,
          py: 4,
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        <Typography color="error" sx={{ mb: 2, maxWidth: 480 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/login', { replace: true })}>
          ログイン画面へ
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CircularProgress />
    </Box>
  );
};

export default AuthCallback;
